import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../../api';
import AdminLayout from './AdminLayout';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import { format } from 'date-fns';
import { RefreshCw } from 'lucide-react';

const statusBadge = {
  paid: 'bg-green-100 text-green-800',
  pending_configuration: 'bg-amber-100 text-amber-800',
  processing: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
};

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/api/admin/payouts');
      setPayouts(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/api/admin/payouts/run-weekly');
      toast({
        title: 'Weekly payout batch ran',
        description: `${data.processed} driver(s) processed — ${data.paid} paid, ${data.pending_configuration} pending configuration, ${data.failed} failed.`,
      });
      await load();
    } catch (err) {
      toast({ title: 'Could not run payouts', description: formatApiError(err), variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  const totalPaid = payouts.filter((p) => p.status === 'paid').reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Driver Payouts</h1>
          <p className="text-muted-foreground text-sm">Weekly RazorpayX bank transfers of completed-trip earnings</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-muted-foreground uppercase">Total paid out</p>
            <p className="text-2xl font-bold text-green-600">₹{totalPaid}</p>
          </div>
          <Button onClick={handleRunNow} disabled={running} data-testid="run-weekly-payouts-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${running ? 'animate-spin' : ''}`} />
            {running ? 'Running…' : 'Run Weekly Payouts Now'}
          </Button>
        </div>
      </div>

      <Card className="p-4 mb-6 bg-accent/10 border-accent/30">
        <p className="text-sm text-primary">
          Real bank transfers require a RazorpayX business payout account (separate KYB from standard
          Razorpay Payments) with <code className="font-mono">RAZORPAYX_KEY_ID</code>, <code className="font-mono">RAZORPAYX_KEY_SECRET</code>
          {' '}and <code className="font-mono">RAZORPAYX_ACCOUNT_NUMBER</code> configured. Until then, payouts are
          recorded as "Pending Configuration" so no earnings are lost — the same batch runs automatically every Monday.
        </p>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Loading…</div>
        ) : payouts.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground" data-testid="no-payouts-admin-note">No payouts recorded yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-background text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Driver</th>
                <th className="px-4 py-3 font-semibold">Period</th>
                <th className="px-4 py-3 font-semibold">Trips</th>
                <th className="px-4 py-3 font-semibold">Amount</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Note</th>
                <th className="px-4 py-3 font-semibold">Created</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="px-4 py-3">{p.driver_name} <span className="text-xs text-muted-foreground font-mono">({p.driver_public_id})</span></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {format(new Date(p.period_start), 'MMM dd')} - {format(new Date(p.period_end), 'MMM dd')}
                  </td>
                  <td className="px-4 py-3">{p.trip_count}</td>
                  <td className="px-4 py-3 font-semibold">₹{p.amount}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge[p.status] || 'bg-muted text-foreground'}`}>
                      {p.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs">{p.failure_reason || '—'}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {p.created_at ? format(new Date(p.created_at), 'MMM dd HH:mm') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </AdminLayout>
  );
}
